// src/pages/Patient/components/UpdatePatient.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPersonById, updatePersonById } from '../../../redux/actions/personActions';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useModalFeedback } from '../../../context/ModalContext';
import InputMask from 'react-input-mask-next';

import AddressSection from '../../Patient/components/AddressSection';
import { validarCPF, validateRGDigits } from '../../../utils/Functions/formatPersons';

import '../styles/CreateNutritionist.css';
import '../../../styles/RBStyle.css';
import '../../../styles/Avatar.css';
import avatarDefault from '../../../assets/img/avatar/avatar.png';
import LoadingEllipsis from '../../../components/LoadingEllipsis/LoadingEllipsis';

function UpdateNutritionist() {
  const { person_id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showInfo, confimationModal, setIsDeleting, hideModal } = useModalFeedback();

  const [selectedLogo, setSelectedLogo] = useState('');
  const [file, setFile] = useState(null);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  const { currentPerson } = useSelector(state => state.persons);


  const [initialValues, setInitialValues] = useState({
    first_name: '',
    last_name: '',
    email: '',
    email2: '',
    phone: '',
    phone2: '',
    mobile: '',
    mobile2: '',
    birth_date: '',
    gender: '',
    person_type: '',
    status: '',
    group_id: '',
    cpf: '',
    rg: '',
    rgi: '',
    ro: '',
    ro_number: '',
    cnh: '',
    cnh_category: '',
    naturalness: '',
    marital_status: '',
    nationality: '',
    religion: '',
    social_media1: '',
    social_media2: '',
    social_media3: '',
    observations: '',
    file_path: '',
    address: {
      zipcode: '',
      street: '',
      number: '',
      neighborhood: '',
      city: '',
      uf: ''
    }
  });

  useEffect(() => {
    if (person_id) {
      dispatch(fetchPersonById(person_id));
    }
    // console.log('person_id recebido do GetPatient useEffect: ', person_id);
  }, [dispatch, person_id]);


  useEffect(() => {
    if (currentPerson && currentPerson.person_id === Number(person_id)) {
      setInitialValues({
        ...currentPerson,
        birth_date: formatDateForInput(currentPerson.birth_date)
      });
      setSelectedLogo(currentPerson.file_path || '');
      setLoading(false); // Só libera o loading se a pessoa certa foi carregada
    }
  }, [currentPerson, person_id]);

  const setImgFile = (e, setFieldValue) => {
    const file = e.target.files[0];
    setFile(file);
    setFieldValue(file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedLogo(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().split('T')[0];
  };

  const validationSchema = Yup.object({
    first_name: Yup.string().required('Primeiro nome é necessário'),
    last_name: Yup.string().required('Sobrenome é necessário'),
    email: Yup.string().email('Email inválido').required('Email é necessário'),
    birth_date: Yup.date()
      .max(new Date(), 'A data de nascimento não pode ser futura.')
      .required('Data de nascimento é necessária'),
    gender: Yup.string().required('Gênero é necessário'),
    person_type: Yup.string().required('Tipo de pessoa é necessário'),
    cpf: Yup.string()
      .required('CPF é necessário')
      .test('cpf-format', 'Formato inválido (use: 000.000.000-00)', value => {
        return /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(value);
      })
      .test('cpf-valid', 'CPF inválido', value => {
        return validarCPF(value);
      }),
    rg: Yup.string().required('RG é necessário')

  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const formData = new FormData();
      for (const key in values) {
        if (key === 'address') {
          for (const addrKey in values[key]) {
            formData.append(`address[${addrKey}]`, values[key][addrKey]);
          }
        } else {
          formData.append(key, values[key]);
        }
      }
      if (file) {
        formData.append('avatar', file);
      }
      await dispatch(updatePersonById({ person_id: person_id, formData })).unwrap();
      // setMessage('Nutricionista atualizada(o) com sucesso!');
      // setShowModal(true);
      // setTimeout(() => {
      //   setShowModal(false);
      //   resetForm();
      //   navigate('/home/nutritionist/get');
      // }, 2000);
      showInfo('Nutricionista atualizada(o) com sucesso!');
      resetForm();
      navigate('/home/nutritionist/get');
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      const errorMsg = typeof error === 'string'
        ? error
        : 'Erro ao enviar formulário. Tente novamente mais tarde.';
      // setMessage(errorMsg);
      // setShowModal(true);
      // setTimeout(() => {
      //   setShowModal(false);
      // }, 2500);
      setErrors({ submit: errorMsg });
      showInfo(errorMsg, 3000);
    }

    setSubmitting(false);
  };

  if (loading) {
    return <LoadingEllipsis message='Carregando Nutricionista' />;
  }

  if (!currentPerson) {
    return <div>Nutricionista não encontrada(o)..</div>;
  }

  const handleBack = (e) => {
    e.preventDefault();
    navigate('/home/nutritionist/get');
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };


  return (
    <>
      <h3>Atualizar Nutricionista</h3>
      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, isSubmitting }) => (
          <Form className='patientForm' encType="multipart/form-data" onKeyDown={handleKeyDown}>

            <div className="patientMain">
              <div className="patientColA">

                <div className="silhuette">
                  <div className="row">
                    <div>
                      <input
                        id="avatar"
                        className="photo"
                        type="file"
                        name="avatar"
                        accept="image/*"
                        onChange={(e) => setImgFile(e, setFieldValue)}
                      />
                      <div>
                        <img id="avatarImg" className="cadphoto" src={selectedLogo} alt="Avatar" />
                      </div>
                    </div>
                    <div className="photo">
                      <label htmlFor="avatar" className="photo">
                        <img className="photo" src="/apple.ico" alt="Upload Avatar" />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="patientColB coluna-rolavel">
                {/* Tipo de Pessoa*/}

                <div id="personType" className="patient-form-group">
                  <div className="trSelects">
                    <div className="col1">
                      <label htmlFor="person_type">Tipo de Pessoa:</label>
                      <Field as="select"
                        name="person_type"
                        id="person_type"
                      >
                        <option value="">Selecione</option>
                        <option value="patient">Paciente</option>
                        <option value="nutritionist">Nutricionista</option>
                        <option value="doctor">Médico</option>
                        <option value="professional">Profissional</option>
                        <option value="user">Usuário</option>
                        <option value="employee">Funcionário</option>
                        <option value="supplier">Fornecedor</option>
                        <option value="partner">Parceiro</option>
                        <option value="owner">Proprietário</option>
                        <option value="other">Outro</option>
                      </Field>
                      <ErrorMessage name="person_type" component="div" className="error-message" />
                    </div>
                    <div className="col2">
                      <label htmlFor="status">Status:</label>
                      <Field as="select"
                        name="status"
                        id="status"
                        defaultValue="active"
                      >
                        <option value="">Selecione</option>
                        <option value="active">Ativo</option>
                        <option value="inactive">Inativo</option>
                      </Field>
                      <ErrorMessage name="status" component="div" className="error-message" />
                    </div>
                    <div className="col3">
                      {/* <label htmlFor="group_id">Tipo de Grupo:</label> */}
                      <Field as="select"
                        name="group_id"
                        id="group_id"
                        defaultValue="1"
                        hidden="true"
                      >
                        <option value="">Selecione</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                      </Field>
                      <ErrorMessage name="group_id" component="div" className="error-message" />
                    </div>
                  </div>
                </div>



                {/* Nome / Sobrenome */}
                <h5>Nome</h5>
                <div className="patient-form-group">
                  <label htmlFor="first_name">Primeiro Nome:</label>
                  <Field
                    type="text"
                    name="first_name"
                    id="first_name"
                    placeholder="Digite o primeiro nome"
                  />
                  <ErrorMessage name="first_name" component="div" className="error-message" />
                </div>

                <div className="patient-form-group">
                  <label htmlFor="last_name">Sobrenome:</label>
                  <Field
                    type="text"
                    name="last_name"
                    id="last_name"
                    placeholder="Digite o sobrenome"
                  />
                  <ErrorMessage name="last_name" component="div" className="error-message" />
                </div>

                {/* Data de Nascimento e Gênero */}
                <div className='pfgLine'>

                  <div className="patient-form-group">
                    <div className="patient-form-group">
                      <label htmlFor="date_birth">Data de Nascimento:</label>
                    </div>
                    <Field
                      type="date"
                      name="birth_date"
                      id="birth_date"
                    />
                    <ErrorMessage name="birth_date" component="div" className="error-message" />
                  </div>
                  <div className="patient-form-group">
                    <label htmlFor="gender">Gênero:</label>

                    <div className="rbstyle">

                      <Field id="female" type="radio" name="gender" value="female" />
                      <label htmlFor="female">Feminino</label>

                      <Field id="male" type="radio" name="gender" value="male" />
                      <label htmlFor="male">Masculino</label>

                      <Field id="others" type="radio" name="gender" value="others" />
                      <label htmlFor="others">Outros</label>
                    </div>
                    <ErrorMessage name="gender" component="div" className="error-message" />
                  </div>
                </div>

                {/* Documentos */}
                <h5>Documentos</h5>
                <div className="patient-form-group">
                  <div className="trSelects">
                    <div className="pfgCol1">
                      <label htmlFor="cpf">CPF:</label>
                      <Field name="cpf">
                        {({ field }) => (
                          <InputMask
                            {...field}
                            mask="999.999.999-99"
                            placeholder="Digite o CPF"
                            onChange={(e) => {
                              setFieldValue("cpf", e.target.value);
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="cpf" component="div" className="error-message" />
                    </div>
                    <div className="pfgCol2">
                      <label htmlFor="rg">RG:</label>
                      <Field
                        type="text"
                        name="rg"
                        id="rg"
                        placeholder="Documento R.G"
                      >
                        {({ field }) => (
                          <InputMask
                            {...field}
                            mask="99.999.999-9"
                            placeholder="Digite o RG"
                            onChange={(e) => {
                              setFieldValue("rg", e.target.value);
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="rg" component="div" className="error-message" />
                    </div>
                    <div className="pfgCol3">
                      <label htmlFor="rgi">RGI</label>
                      <Field
                        type="text"
                        name="rgi"
                        id="rgi"
                        placeholder="Documento R.G.I"
                      />
                      <ErrorMessage name="rgi" component="div" className="error-message" />
                    </div>
                  </div>
                </div>
                {/* 
              // Campos de CNH - Carteira Nacional de Habilitação
              <div className="patient-form-group">
                <div className="trSelects">
                  <div className="pfgCol1">
                    <label htmlFor="cnh">CNH nº:</label>
                    <Field
                      type="text"
                      name="cnh"
                      id="cnh"
                      placeholder="C.N.H"
                    />
                    <ErrorMessage name="cnh" component="div" className="error-message" />
                  </div>
                  <div className="pfgCol2">
                    <label htmlFor="cnh_category">CNH Categoria:</label>
                    <Field as="select"
                      name="cnh_category"
                      id="cnh_category"
                    >
                      <option value="">Selecione</option>
                      <option value="A">A</option>
                      <option value="B">B</option>
                      <option value="C">C</option>
                      <option value="D">D</option>
                      <option value="E">E</option>
                    </Field>
                    <ErrorMessage name="cnh_category" component="div" className="error-message" />
                  </div>
                  <div className="pfgCol3">
                  </div>
                </div>
              </div> */}


                {/* Registro Profissional - RP */}
                {/* 
              <div className="patient-form-group">
                <div className="trSelects">
                  <div className="pfgCol1">
                    <label htmlFor="ro_number">R.O nº:</label>
                    <Field
                      type="text"
                      name="ro_number"
                      id="ro_number"
                      placeholder="Registro de Ordem"
                    />
                    <ErrorMessage name="ro_number" component="div" className="error-message" />
                  </div>
                  <div className="pfgCol2">
                    <label htmlFor="ro">Registro Profissional da Ordem:</label>
                    <Field as="select"
                      name="ro"
                      id="ro"
                    >
                      <option value="">Selecione</option>
                      <option value="CRN">CRN - Conselho Regional de Nutricionistas</option>
                      <option value="CFN">CFN - Conselho Federal de Nutricionistas</option>
                      <option value="CRM">CRM - Conselho de Medicina</option>
                      <option value="CRE">Conselho de Enfermagem</option>
                      <option value="COFEN">COFEN - Conselho Federal de Enfermagem</option>
                      <option value="COREN-SP">COREN-SP</option>
                    </Field>
                    <ErrorMessage name="ro" component="div" className="error-message" />
                  </div>
                  <div className="pfgCol3">
                    <label htmlFor="speciality">Especialidade:</label>
                    <Field as="select"
                      name="speciality"
                      id="speciality"
                    >
                      <option value="">Selecione</option>
                      <option value="nutrition">Nutricionista</option>
                      <option value="general_doctor">Clínico Geral</option>
                      <option value="physiotherapist">Fisioterapeuta</option>
                    </Field>
                    <ErrorMessage name="ro" component="div" className="error-message" />
                  </div>
                </div>
              </div> */}




                {/* Campos de contato */}
                <h5>Contatos</h5>
                <div className="patient-form-group">
                  <div className="trSelects">
                    <div className="pfgCol1">
                      <label htmlFor="email2">Email:</label>
                      <Field
                        type="email"
                        name="email"
                        id="email"
                        placeholder="Digite o email principal"
                      />
                      <ErrorMessage name="email" component="div" className="error-message" />
                    </div>
                    <div className="pfgCol2">
                      <label htmlFor="email2">Email Secundário:</label>
                      <Field
                        type="email"
                        name="email2"
                        id="email2"
                        placeholder="Digite o email secundário"
                      />
                      <ErrorMessage name="email2" component="div" className="error-message" />
                    </div>

                    <div className="pfgCol3">
                    </div>
                  </div>
                </div>

                <div className="patient-form-group">
                  <div className="trSelects">
                    <div className="pfgCol1">
                      <label htmlFor="phone">Telefone:</label>
                      <Field name="phone">
                        {({ field }) => (
                          <InputMask
                            {...field}
                            mask="(99) 99999-9999"
                            placeholder="Digite o telefone"
                            onChange={(e) => {
                              setFieldValue("phone", e.target.value);
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="phone" component="div" className="error-message" />
                    </div>
                    <div className="pfgCol2">
                      <label htmlFor="phone2">Telefone Secundário:</label>
                      <Field name="phone2">
                        {({ field }) => (
                          <InputMask
                            {...field}
                            mask="(99) 99999-9999"
                            placeholder="Digite o telefone secundário"
                            onChange={(e) => {
                              setFieldValue("phone2", e.target.value);
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="phone2" component="div" className="error-message" />
                    </div>
                    <div className="pfgCol3">
                    </div>
                  </div>
                </div>

                <div className="patient-form-group">
                  <div className="trSelects">
                    <div className="pfgCol1">
                      <label htmlFor="mobile">Celular:</label>
                      <Field name="mobile">
                        {({ field }) => (
                          <InputMask
                            {...field}
                            mask="(99) 99999-9999"
                            placeholder="Digite o celular"
                            onChange={(e) => {
                              setFieldValue("mobile", e.target.value);
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="mobile" component="div" className="error-message" />
                    </div>
                    <div className="pfgCol2">
                      <label htmlFor="mobile2">Celular Secundário:</label>
                      <Field name="mobile2">
                        {({ field }) => (
                          <InputMask
                            {...field}
                            mask="(99) 99999-9999"
                            placeholder="Digite o celular secundário"
                            onChange={(e) => {
                              setFieldValue("mobile2", e.target.value);
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage name="mobile2" component="div" className="error-message" />
                    </div>
                    <div className="pfgCol3">
                    </div>
                  </div>
                </div>

                <div className="patient-form-group">
                  <div className="trSelects">
                    <div className="pfgCol1">
                      <label htmlFor="social_media1">Facebook</label>
                      <Field
                        type="text"
                        name="social_media1"
                        id="social_media1"
                        placeholder="Facebook"
                      />
                      <ErrorMessage name="social_media1" component="div" className="error-message" />
                    </div>
                    <div className="pfgCol2">
                      <label htmlFor="social_media2">Instagram</label>
                      <Field
                        type="text"
                        name="social_media2"
                        id="social_media2"
                        placeholder="Instagram"
                      />
                      <ErrorMessage name="social_media2" component="div" className="error-message" />
                    </div>
                    <div className="pfgCol3">
                      <label htmlFor="social_media3">X (twitter)</label>
                      <Field
                        type="text"
                        name="social_media3"
                        id="social_media3"
                        placeholder="X"
                      />
                      <ErrorMessage name="social_media3" component="div" className="error-message" />
                    </div>
                  </div>
                </div>

                <div className="patient-form-group">
                  <Field as="textarea"
                    name="observations"
                    id="observations"
                    placeholder="Obs:"
                    rows={2} // Defina o número de linhas inicial do textarea
                  />
                  <ErrorMessage name="observations" component="div" className="error-message" />
                </div>

                {/* Campos de endereço */}
                <AddressSection />
              </div>

              <div className="patientColC">
                <ErrorMessage name="cpf" component="div" className="spanMessage" />
                <ErrorMessage name="person_type" component="div" className="spanMessage" />
                <ErrorMessage name="first_name" component="div" className="spanMessage" />
                <ErrorMessage name="last_name" component="div" className="spanMessage" />
                <ErrorMessage name="email" component="div" className="spanMessage" />
                <ErrorMessage name="birth_date" component="div" className="spanMessage" />
                <ErrorMessage name="gender" component="div" className="spanMessage" />
                {errors.first_name && <div className="spanMessage">{errors.first_name}</div>}
                <div className="personButtons">
                  <button className='sendBtn' type='submit' disabled={isSubmitting}>Enviar</button>
                </div>
                <div className="personButtons">
                  <button className='backBtn' onClick={handleBack}>Cancelar</button>
                </div>
              </div>

            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

export default UpdateNutritionist;
